/**
 * @file AddMemberDataContent.js
 * @module src/js/components/AddMemberDataContent
 * @description
 * **A reusable component for adding or editing member data in the EDALab frontend.**
 * - Accepts various member data fields and their corresponding setter functions as props.
 * - Displays input fields for member ID, institute, level, password, name, and profile image.
 * - Shows validation messages for required fields and password confirmation.
 *
 * @version
 * <p>
 *  2025-11-28: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Add 'choose' and -1 as default values for institute and level. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Add memberIDRef to focus the Member ID input on mount and after submission. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Move messages to the modal system. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add show/hide toggles for password fields. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-05: Migrate to useAddMember hook for adding members. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useMemo, useState } from "react";
import useAddMember from "../hooks/useAddMember";
import ChooseAFile from "./ChooseAFile";
import { InputInvalidMessage, StyledImg } from "./UtilityComponents";
import { Eye, EyeOff } from "lucide-react";

export default function AddMemberDataContent({
    headerRef,
    memberIDRef
}) {
    const [memberID, setMemberID] = useState('');
    const [institute, setInstitute] = useState('choose');
    const [level, setLevel] = useState(-1);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordForConfirmation, setPasswordForConfirmation] = useState('');
    const [showPasswordForConfirmation, setShowPasswordForConfirmation] = useState(false);
    const [name, setName] = useState('');
    const [imgFile, setImgFile] = useState([null]);
    const [updating, setUpdating] = useState(false);
    const addMember = useAddMember({
        setMemberID,
        setInstitute,
        setLevel,
        setPassword,
        setPasswordForConfirmation,
        setName,
        setImgFile,
        memberIDRef,
        setUpdating,
    });

    const body = useMemo(() => ({
        id: memberID,
        institute,
        level,
        pass: password,
        name,
        photo: imgFile[0] ? imgFile[0].name : 'none'
    }), [memberID, institute, level, password, name, imgFile]);

    return (
        <>
            <div className="content">
                <b>Username</b>&nbsp;
                <InputInvalidMessage condition={memberID === ''} />
                <input
                    type="text"
                    placeholder="ywchang"
                    ref={memberIDRef}
                    value={memberID || ''}
                    onChange={(e) => setMemberID(e.target.value)}
                    style={{ marginTop: '5px', marginBottom: '5px' }}
                />

                <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                    <b>Lab Mail:</b> {memberID !== '' ? `${memberID}@eda.ee.ntu.edu.tw` : ''}
                </div>

                <b>Password</b>&nbsp;
                <InputInvalidMessage condition={password === ''} />
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginTop: '5px',
                        marginBottom: '5px'
                    }}
                >
                    <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Xyz_13579"
                        value={password || ''}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ flex: 1 }}
                    />
                    <span
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowPassword((prev) => !prev)}
                    >
                        {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                    </span>
                </div>

                <b>Password Confirmation</b>&nbsp;
                <InputInvalidMessage condition={passwordForConfirmation === ''} />
                <InputInvalidMessage
                    condition={passwordForConfirmation !== '' && password !== passwordForConfirmation}
                    message='The passwords do not match.'
                />
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginTop: '5px',
                        marginBottom: '5px'
                    }}
                >
                    <input
                        type={showPasswordForConfirmation ? 'text' : 'password'}
                        placeholder="Xyz_13579"
                        value={passwordForConfirmation || ''}
                        onChange={(e) => setPasswordForConfirmation(e.target.value)}
                        style={{ flex: 1 }}
                    />
                    <span
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowPasswordForConfirmation((prev) => !prev)}
                    >
                        {showPasswordForConfirmation ? <EyeOff size={22} /> : <Eye size={22} />}
                    </span>
                </div>

                <b>Full Name</b>&nbsp;
                <InputInvalidMessage condition={name === ''} />
                <input
                    type="text"
                    placeholder="張耀文"
                    value={name || ''}
                    onChange={(e) => setName(e.target.value)}
                    style={{ marginTop: '5px', marginBottom: '5px' }}
                />

                <b>Institute</b>&nbsp;
                <InputInvalidMessage condition={institute === 'choose'} />
                <select
                    value={institute}
                    onChange={(e) => setInstitute(e.target.value)}
                    style={{ marginTop: '5px', marginBottom: '5px' }}
                >
                    <option value="choose" disabled>-- Please choose an institute --</option>
                    <option value="電子所">電子所</option>
                    <option value="電機所">電機所</option>
                    <option value="資工所">資工所</option>
                    <option value="重點科技學院">重點科技學院</option>
                    <option value="電資學院資料科學學程">電資學院資料科學學程</option>
                </select>

                <b>Current Degree</b>&nbsp;
                <InputInvalidMessage condition={level === -1} />
                <select
                    value={level}
                    onChange={(e) => setLevel(parseInt(e.target.value))}
                    style={{ marginTop: '5px', marginBottom: '5px' }}
                >
                    <option value={-1} disabled>-- Please choose a degree --</option>
                    <option value={1}>M.S.</option>
                    <option value={2}>Ph.D.</option>
                    <option value={4}>Postdoc</option>
                </select>

                <b>Profile Image</b>&nbsp;
                <span style={{ fontSize: '0.8em' }}>
                    (Optional. Upload the profile image now or let the new member upload it later.)
                </span>
                <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                    <ChooseAFile
                        accept={'.jpg, .jpeg, .png'} // need a space after comma
                        files={imgFile}
                        setFiles={setImgFile}
                        alwaysDark={true}
                    />
                </div>

                <ul className="actions special">
                    <li>
                        <button
                            onClick={async () => {
                                await addMember({ body, imgFile: imgFile[0] });
                                setTimeout(() => headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
                            }}
                            style={{ marginTop: '15px' }}
                            disabled={
                                memberID === '' || password === '' ||
                                password !== passwordForConfirmation ||
                                name === '' || updating
                            }
                        >
                            Add
                        </button>
                    </li>
                </ul>
            </div>

            <span className="image" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <StyledImg
                    src={imgFile[0] ? URL.createObjectURL(imgFile[0]) : null}
                    style={{
                        borderRadius: '5px',
                        boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)'
                    }}
                />
            </span>
        </>
    );
}